/**
 * Date and time formatting in the interface language.
 *
 * Angular's `date` pipe formats for the app-wide `LOCALE_ID`, which is fixed at
 * startup and needs each language's locale data bundled. These helpers use
 * `Intl` instead, so every interface language formats natively and a language
 * switch applies without a reload. Templates use `LocalizedDatePipe`; code reads
 * the locale from `LanguageService.locale()`, or `DATE_LOCALE` in the shared layer.
 */

import { InjectionToken, Signal } from '@angular/core';

/**
 * The `Intl` locale for dates and times in the interface language, as a signal
 * that follows a language switch. It's `LanguageService.locale`, made available
 * to the shared layer by `provideDateLocale()`.
 */
export const DATE_LOCALE = new InjectionToken<Signal<string>>('DATE_LOCALE');

/**
 * Representative locale per interface language, for when none of the browser's
 * preferred languages is in it. European languages use a 24-hour clock and
 * Monday-first weeks; English uses the US conventions.
 */
const FALLBACK_LOCALE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  hu: 'hu-HU',
  it: 'it-IT',
  pl: 'pl-PL',
  // The translations are Brazilian Portuguese (see translate.py).
  pt: 'pt-BR',
  tr: 'tr-TR',
};

/**
 * Named formats. Those named after a preset of Angular's `date` pipe print the
 * same in `en-US`; the comments show the `en-US` output.
 */
export const DATE_FORMATS = {
  /** `10/20/26, 2:05 PM` */
  short: { dateStyle: 'short', timeStyle: 'short' },
  /** `2:05 PM` */
  shortTime: { timeStyle: 'short' },
  /** `Oct 20, 2026` */
  mediumDate: { dateStyle: 'medium' },
  /** `Tue`, like Angular's `EEE` */
  shortWeekday: { weekday: 'short' },
  /** `10/20/2026, 2:05:33 PM`, like `Date.toLocaleString()` without options */
  timestamp: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

/** A named format, or `Intl` options for a one-off. */
export type DateFormat = keyof typeof DATE_FORMATS | Intl.DateTimeFormatOptions;

export type DateInput = Date | number | string;

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

const baseLanguage = (tag: string): string => tag.toLowerCase().split(/[-_]/)[0];

/** The tag in canonical form, or `null` when `Intl` can't use it. */
function canonicalLocale(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * The `Intl` locale for an interface language. The first of the browser's
 * preferred languages in that language wins, as its region decides the date
 * order, week start and clock (`de-AT` for a German interface). Otherwise a
 * representative locale, so a German interface on an `en-US` browser doesn't
 * format dates the American way.
 */
export function resolveLocale(
  language: string,
  preferred: readonly string[] = browserLanguages()
): string {
  const base = baseLanguage(language);
  if (!base) return 'en';
  for (const tag of preferred) {
    const locale = baseLanguage(tag) === base ? canonicalLocale(tag) : null;
    if (locale) return locale;
  }
  return FALLBACK_LOCALE[base] ?? canonicalLocale(language) ?? 'en';
}

// Creating an `Intl.DateTimeFormat` costs far more than formatting with one, so
// they are reused. There are only a few formats per locale.
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(format: DateFormat, locale: string): Intl.DateTimeFormat {
  const options = typeof format === 'string' ? DATE_FORMATS[format] : format;
  const key = `${locale} ${typeof format === 'string' ? format : JSON.stringify(format)}`;
  let result = formatters.get(key);
  if (!result) {
    result = new Intl.DateTimeFormat(locale, options);
    formatters.set(key, result);
  }
  return result;
}

/** Formats a date for a locale; empty for a missing or invalid date. */
export function formatDate(
  value: DateInput | null | undefined,
  format: DateFormat,
  locale: string
): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatter(format, locale).format(date);
}
